import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { Save, Briefcase, Shield, Banknote, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Field config per tab: [key, label, suffix?]
const MTF_FIELDS = [
  ['outstanding', 'Outstanding', '₹'],
  ['funding_stock_value', 'Funding Stock Value', '₹'],
  ['securities_value', 'Securities Value', '₹'],
  ['safety_cover_percent', 'Safety Cover %', '%'],
  ['safety_cover_allowed', 'Safety Cover Allowed %', '%'],
  ['limit', 'Sanction Limit', '₹'],
  ['used', 'Used', '₹'],
];

const LAS_FIELDS = [
  ['pledged_value', 'Pledged Value', '₹'],
  ['eligible_loan', 'Eligible Loan', '₹'],
  ['ltv_ratio', 'LTV Ratio (0-1)', ''],
];

const BAJAJ_LOAN_FIELDS = [
  ['amount_used', 'Amount Used', '₹'],
  ['available', 'Available', '₹'],
  ['sanction_limit', 'Sanction Limit', '₹'],
  ['margin_percent', 'Margin %', '%'],
  ['ltv_percent', 'LTV %', '%'],
  ['deficit', 'Deficit', '₹'],
];

const BAJAJ_STATUS_FIELDS = [
  ['limit', 'Limit', '₹'],
  ['used', 'Used', '₹'],
  ['available', 'Available', '₹'],
];

const BAJAJ_BANKER_FIELDS = [
  ['banker_available', 'Banker Available', '₹'],
  ['banker_used', 'Banker Used', '₹'],
  ['used_percent', 'Used %', '%'],
];

// Format a value for display in an input. Uses Indian numbering (lakh/crore
// commas) for whole-rupee amounts, e.g. 17581260 → "1,75,81,260". Keeps
// ratios precise (anything < 100 like LTV 0.5 stays as "0.5").
const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '';
  // Strip any existing commas so we can re-format consistently
  const cleaned = typeof v === 'string' ? v.replace(/,/g, '') : v;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return String(v);
  if (Math.abs(n) >= 100) return Math.round(n).toLocaleString('en-IN');
  return parseFloat(n.toFixed(4)).toString();
};

// Strip commas from user-typed input so we store the raw numeric string.
// Allows the user to keep a partial entry like "17,58," (mid-typing) by
// converting it back to "1758" until they add another digit.
const stripCommas = (v) => (typeof v === 'string' ? v.replace(/,/g, '') : v);

const BajajUpdate = ({ user, setUser, embedded = false }) => {
  const [activeTab, setActiveTab] = useState('mtf');
  const [data, setData] = useState({ mtf: [], las: [], bajajLoan: [], bajajStatus: [], bajajBanker: [] });
  const [edited, setEdited] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [mtfRes, lasRes, loanRes, statusRes, bankerRes] = await Promise.all([
        api.get('/debt/mtf-status'),
        api.get('/debt/las-accounts'),
        api.get('/debt/bajaj-loans'),
        api.get('/debt/bajaj-status'),
        api.get('/debt/bajaj-banker'),
      ]);
      setData({
        mtf: mtfRes.data || [], las: lasRes.data || [],
        bajajLoan: loanRes.data || [], bajajStatus: statusRes.data || [], bajajBanker: bankerRes.data || [],
      });
      setEdited({});
    } catch (e) { toast.error('Failed to load Bajaj data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleEdit = (tabKey, recordId, field, value) => {
    // Store the raw numeric string (commas stripped). The fmt() helper will
    // re-add Indian-locale commas on display.
    const cleaned = stripCommas(value);
    setEdited(prev => ({
      ...prev,
      [`${tabKey}:${recordId}`]: { ...(prev[`${tabKey}:${recordId}`] || {}), [field]: cleaned },
    }));
  };

  const dirtyCount = Object.keys(edited).length;

  const saveAll = async () => {
    if (!dirtyCount) { toast.info('No changes to save'); return; }
    if (!window.confirm(`Save ${dirtyCount} record update(s)?`)) return;
    setSaving(true);
    let success = 0, failed = 0;
    for (const [key, changes] of Object.entries(edited)) {
      const [tabKey, recordId] = key.split(':');
      const endpoint = {
        mtf: '/debt/mtf-status', las: '/debt/las-accounts',
        bajajLoan: '/debt/bajaj-loans', bajajStatus: '/debt/bajaj-status', bajajBanker: '/debt/bajaj-banker',
      }[tabKey];
      // Convert numeric fields
      const payload = {};
      for (const [f, v] of Object.entries(changes)) {
        if (v === '' || v === null) payload[f] = null;
        else if (!isNaN(parseFloat(v))) payload[f] = parseFloat(v);
        else payload[f] = v;
      }
      try {
        await api.put(`${endpoint}/${recordId}`, payload);
        success++;
      } catch (e) {
        failed++;
        console.error(`Failed to update ${key}`, e);
      }
    }
    setSaving(false);
    if (failed) toast.error(`${success} saved, ${failed} failed`);
    else toast.success(`${success} record(s) saved`);
    await fetchAll();
  };

  const renderRow = (tabKey, record, fields) => {
    const recordId = record.id;
    const accountLabel = record.account_code || record.member_name || record.bank_name || recordId.slice(0, 8);
    return (
      <div key={recordId} className="card p-3 mb-3" data-testid={`record-${tabKey}-${accountLabel}`}>
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <h3 className="font-semibold text-gray-700">{accountLabel}</h3>
          {record.bank_name && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{record.bank_name}</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {fields.map(([fk, flabel, suffix]) => {
            const editKey = `${tabKey}:${recordId}`;
            const editedVal = edited[editKey]?.[fk];
            const currentVal = editedVal !== undefined ? editedVal : record[fk];
            // Compare cleaned (comma-free) values for dirty detection
            const isDirty = editedVal !== undefined && stripCommas(editedVal) !== stripCommas(fmt(record[fk]));
            return (
              <div key={fk}>
                <label className="text-xs text-gray-500 block mb-1">
                  {flabel} {suffix && <span className="text-gray-400">({suffix})</span>}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fmt(currentVal)}
                  onChange={(e) => handleEdit(tabKey, recordId, fk, e.target.value)}
                  className={`input-field text-sm w-full ${isDirty ? 'bg-yellow-50 border-amber-400' : ''}`}
                  data-testid={`input-${tabKey}-${accountLabel}-${fk}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TABS = [
    { key: 'mtf', label: 'MTF Status', icon: Briefcase, records: data.mtf, fields: MTF_FIELDS, hint: 'Update MTF outstanding, securities & safety cover from your daily Bajaj Securities report' },
    { key: 'las', label: 'LAS Records', icon: Shield, records: data.las, fields: LAS_FIELDS, hint: 'Update pledged value & eligible loan after pledge/free transactions' },
    { key: 'bajajLoan', label: 'Bajaj Loans', icon: Banknote, records: data.bajajLoan, fields: BAJAJ_LOAN_FIELDS, hint: 'Update Bajaj Finance loan amount used, deficit, margin %' },
    { key: 'bajajStatus', label: 'Bajaj Status', icon: Banknote, records: data.bajajStatus, fields: BAJAJ_STATUS_FIELDS, hint: 'Update Bajaj limit/used/available' },
    { key: 'bajajBanker', label: 'Banker Data', icon: Banknote, records: data.bajajBanker, fields: BAJAJ_BANKER_FIELDS, hint: 'Update banker-reported available/used' },
  ];

  const activeConfig = TABS.find(t => t.key === activeTab);

  const content = (
    <div data-testid="bajaj-update-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">Debt Daily Update</h1>
            <p className="text-sm text-gray-600">Update LAS / MTF / Bajaj loan status from your daily reports</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchAll} disabled={loading} className="btn-outline text-sm flex items-center gap-1" data-testid="refresh-btn">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              onClick={saveAll}
              disabled={saving || !dirtyCount}
              className="btn-primary text-sm flex items-center gap-1 disabled:opacity-50"
              data-testid="save-all-btn"
            >
              <Save size={14} /> {saving ? 'Saving...' : `Save All${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </button>
          </div>
        </div>

        {/* Tab nav */}
        <div className="flex gap-1 mb-4 border-b overflow-x-auto" data-testid="tab-nav">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-1 border-b-2 transition-colors ${
                activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon size={14} /> {t.label} <span className="text-xs text-gray-400">({t.records.length})</span>
            </button>
          ))}
        </div>

        {activeConfig && (
          <>
            <p className="text-xs text-gray-500 mb-3 italic">{activeConfig.hint}</p>
            {loading ? (
              <div className="text-center py-12 text-gray-500" data-testid="loading">Loading...</div>
            ) : activeConfig.records.length === 0 ? (
              <div className="card text-center py-12 text-gray-500" data-testid="empty">
                No {activeConfig.label} records yet.
              </div>
            ) : (
              activeConfig.records.map(r => renderRow(activeConfig.key, r, activeConfig.fields))
            )}
          </>
        )}

        {dirtyCount > 0 && (
          <div className="fixed bottom-4 right-4 bg-amber-100 border border-amber-300 text-amber-800 px-4 py-2 rounded-lg shadow-lg" data-testid="unsaved-banner">
            ⚠ {dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}
          </div>
        )}
      </div>
  );

  if (embedded) return content;
  return <Layout user={user} setUser={setUser}>{content}</Layout>;
};

export default BajajUpdate;
